import authService from '@services/auth';
import { toast } from 'react-toastify';
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { useTranslation } from '@hooks/useTranslation';

export type UseForgotPasswordResult = {
  forgotPasswordMutation: UseMutationResult<void, Error, string>;
  confirmResetMutation: UseMutationResult<
    void,
    Error,
    {
      email: string;
      confirmationCode: string;
      newPassword: string;
    }
  >;
  isLoading: boolean;
};

export const useForgotPassword = (): UseForgotPasswordResult => {
  const tActionMessages = useTranslation('actionMessages');
  const forgotPasswordMutation = useMutation({
    mutationFn: (email: string) => authService.forgotPassword(email),
    onSuccess: (data: any) => {
      if (data?.message === 'INITIAL_LOGIN') {
        toast.success(`Welcome message re-sent to ${data.destination}`);
        return;
      }
      if (data?.destination) {
        toast.success(`Reset code sent to ${data.destination}`);
        return;
      }
      const errorMessage = data?.errorMessage || tActionMessages.requestError;
      toast.error(errorMessage);
    },
    onError: (e: any) => {
      const msg =
        e?.response?.data?.errorMessage ||
        e?.response?.data?.message ||
        e?.message ||
        tActionMessages.requestError;
      toast.error(msg);
    },
  });

  const confirmResetMutation = useMutation({
    mutationFn: ({
      email,
      confirmationCode,
      newPassword,
    }: {
      email: string;
      confirmationCode: string;
      newPassword: string;
    }) =>
      authService.confirmForgotPassword(email, confirmationCode, newPassword),
    onSuccess: (data: any) => {
      if (data?.confirmation && data?.confirmation === '200') {
        toast.success(tActionMessages.passwordResetSuccessfully);
        return;
      }
      const rawMessage = data?.errorMessage || '';
      if (
        rawMessage.includes('CodeMismatchException') ||
        rawMessage.toLowerCase().includes('invalid verification code')
      ) {
        toast.error(tActionMessages.invalidVerificationCode);
        return;
      }
      toast.error(rawMessage || tActionMessages.requestError);
    },
    onError: (e: any) => {
      const rawMessage =
        e?.response?.data?.errorMessage ||
        e?.response?.data?.message ||
        e?.message ||
        '';
      if (
        rawMessage.includes('CodeMismatchException') ||
        rawMessage.toLowerCase().includes('invalid verification code')
      ) {
        toast.error(tActionMessages.invalidVerificationCode);
        return;
      }
      toast.error(rawMessage || tActionMessages.requestError);
    },
  });

  return {
    forgotPasswordMutation,
    confirmResetMutation,
    isLoading:
      forgotPasswordMutation.isPending || confirmResetMutation.isPending,
  };
};

export default useForgotPassword;
